/*
 * <BeSwatch> -- a colour chip with the BeOS bevel derived from its own colour.
 * A <span>, a <button> when onClick is given, or a labelled colour picker when
 * onColorChange is.
 */

import type { CSSProperties, HTMLAttributes, MouseEventHandler, ReactNode } from "react";
import { type BeCommon, beComponent } from "./create.tsx";

export type BeSwatchSize = "mini" | "small" | "medium" | "large";

/* Not BeProps<E>: the chip is a <span>, a <button> or a <label>-wrapped picker
 * depending on the handlers given, so the generic HTMLAttributes is the floor.
 * `onClick`/`title` are omitted because this component defines its own. */
export type BeSwatchProps = BeCommon &
	Omit<HTMLAttributes<HTMLElement>, "className" | "style" | "onClick" | "title" | "color"> & {
		/** The chip's colour. Any CSS colour; the bevel shades derive from it. */
		color: string;
		size?: BeSwatchSize;
		/** Picker state -- draws the nav-blue selection ring. */
		selected?: boolean;
		onClick?: MouseEventHandler<HTMLButtonElement>;
		/**
		 * Makes the chip a colour PICKER. R5 has no such control -- BColorControl is
		 * a different, much larger thing -- so the OS dialog comes from a native
		 * <input type="color"> hidden behind the chip. Supplying this renders a
		 * <label> wrapping chip + input, so a click anywhere opens the picker.
		 */
		onColorChange?: (value: string) => void;
		/** Text beside the chip. Only meaningful with onColorChange (the <label>). */
		label?: ReactNode;
		title?: string;
	};

export const BeSwatch = beComponent<BeSwatchProps, HTMLElement>("BeSwatch", (props, be) => {
	const { color, size = "small", selected, onClick, onColorChange, label, title, scale, className, style: _style, ...rest } = props;

	const { ref, className: cls, style } = be("be-swatch", size !== "small" && `is-${size}`, selected && "is-selected");
	const css = { ...style, "--be-swatch-color": color } as CSSProperties;

	if (onColorChange) {
		return (
			<label className="be-swatch-field" title={title} {...rest}>
				{label}
				<span className={cls} style={css} ref={ref} />
				{/* not display:none -- that drops it from the tab order, and some
				    engines refuse to open a picker for a hidden input */}
				<input
					type="color"
					className="be-swatch__input"
					value={color}
					onChange={(e) => onColorChange(e.target.value)}
					aria-label={typeof label === "string" ? label : title}
				/>
			</label>
		);
	}

	if (onClick) {
		return (
			<button
				type="button"
				{...rest}
				className={cls}
				style={css}
				ref={ref as React.Ref<HTMLButtonElement>}
				onClick={onClick}
				title={title}
				aria-pressed={selected}
			/>
		);
	}
	return <span {...rest} className={cls} style={css} ref={ref} title={title} />;
});
